import { useState } from "react";
import { useMatches } from "react-router";

import { SkeletonAwait } from "~/components/suspense/skeleton";
import { Skeleton } from "~/components/ui/skeleton";
import { memoizeDerivedPromise } from "~/lib/utils/memoize-derived-promise";

import { NavItem } from "./nav-item";
import type { NavigationCategory } from "./types";
import { buildNavigationSections } from "./utils";

interface MainNavProps {
  categories?: NavigationCategory[] | Promise<NavigationCategory[]>;
  hostname?: string;
  attributes?: Record<string, unknown>;
}

type NavMaps = Record<string, string> | Promise<Record<string, string>>;

/**
 * Pulls categoryTree and hostname from the nearest route match that provides them.
 * This is needed because when Builder's <Content> renders this component,
 * it doesn't pass route loader data as props.
 */
function useNavData() {
  const matches = useMatches();
  for (const match of matches) {
    const data = match.data as Record<string, unknown> | undefined;
    if (data && "categoryTree" in data) {
      return {
        categories:
          (data.categoryTree as
            | NavigationCategory[]
            | Promise<NavigationCategory[]>) ?? [],
        hostname: (data.hostname as string) ?? "",
        lmnAllowed: (data.lmnAllowed as boolean) ?? true,
        lmnEnable: (data.lmnEnable as boolean) ?? true,
        // id → all-state ("Shop All") landing, c_alternativeUrl else c_shopAll,
        // host-normalized to a relative path (SFCC-11632). The nav's "Shop All"
        // link reads this so it matches the breadcrumb's all-state target
        // (e.g. health-condition → /health-condition/shop-all via c_shopAll).
        categoryAltUrlMap: (data.categoryAltUrlMap as NavMaps) ?? {},
      };
    }
  }
  return {
    categories: [] as NavigationCategory[],
    hostname: "",
    lmnAllowed: true,
    lmnEnable: true,
    categoryAltUrlMap: {} as Record<string, string>,
  };
}

type ResolvedNav = {
  cats: NavigationCategory[];
  altUrlMap: Record<string, string>;
};

/**
 * Survive a remount / new Promise so the menu does not collapse to empty.
 *
 * CLIENT-ONLY (see `rememberNav`): on the server this module is shared by
 * every request in the isolate, so remembering here would leak one request's
 * resolved nav into another request's SSR Suspense fallback — stale at best,
 * and on multi-site workers (dev/preview) the WRONG SITE's menu. Server
 * renders always fall back to the skeleton until their own tree streams in.
 */
let lastResolvedNav: ResolvedNav | null = null;

/**
 * COM-405 / COM-406: memoized by SOURCE identity (not `useMemo`) so every
 * render — including a suspend retry, whose work-in-progress React discards —
 * hands `<Await>` the same derived promise for the same loader promises. A
 * render-fresh promise here puts every router state update into an infinite
 * suspend/retry loop; see `~/lib/utils/memoize-derived-promise`.
 */
const resolveNav = memoizeDerivedPromise(
  (
    cats: NavigationCategory[] | Promise<NavigationCategory[]>,
    altUrlMap: NavMaps,
  ): ResolvedNav | Promise<ResolvedNav> => {
    if (cats instanceof Promise || altUrlMap instanceof Promise) {
      return Promise.all([cats, altUrlMap]).then(
        ([resolvedCats, resolvedAlt]) =>
          rememberNav({
            cats: resolvedCats,
            altUrlMap: resolvedAlt,
          }),
      );
    }
    return rememberNav({ cats, altUrlMap });
  },
);

function rememberNav(nav: ResolvedNav): ResolvedNav {
  if (typeof document !== "undefined" && nav.cats.length) {
    lastResolvedNav = nav;
  }
  return nav;
}

function MainNavSkeleton() {
  return (
    <div
      className="min-h-[20rem]"
      aria-busy="true"
      aria-label="Loading navigation"
    >
      {[0, 1].map(section => (
        <div
          key={section}
          className={section > 0 ? "mt-[1.625rem]" : undefined}
        >
          <Skeleton className="mb-[0.625rem] h-6 w-40" />
          <div className="space-y-1">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function NavSections({
  cats,
  altUrlMap,
  host,
  lmnAllowed,
  lmnEnable,
  openNavItem,
  onToggle,
}: {
  cats: NavigationCategory[];
  altUrlMap: Record<string, string>;
  host: string;
  lmnAllowed: boolean;
  lmnEnable: boolean;
  openNavItem: string | null;
  onToggle: (id: string) => void;
}) {
  const sections = buildNavigationSections(cats, host, lmnAllowed, lmnEnable);
  return (
    <>
      {sections.map(
        ({ sectionTitle, categories: sectionCats }, sectionIndex) => (
          <div key={sectionTitle}>
            <h4
              className={`body-header-common mb-[0.625rem] text-lg font-bold text-blue-05${
                sectionIndex > 0 ? " mt-[1.625rem]" : ""
              }`}
            >
              {sectionTitle}
            </h4>
            <div>
              {sectionCats.map(category => (
                <NavItem
                  key={category.id}
                  category={category}
                  categoryAltUrlMap={altUrlMap}
                  isOpen={openNavItem === category.id}
                  onToggle={() => onToggle(category.id)}
                />
              ))}
            </div>
          </div>
        ),
      )}
    </>
  );
}

export function MainNav({ categories, hostname, attributes }: MainNavProps) {
  const routeData = useNavData();
  const catsSource =
    Array.isArray(categories) && categories.length
      ? categories
      : categories instanceof Promise
        ? categories
        : routeData.categories;
  const host = hostname || routeData.hostname;

  const [openNavItem, setOpenNavItem] = useState<string | null>(null);

  const handleToggle = (id: string) => {
    setOpenNavItem(prev => (prev === id ? null : id));
  };

  const nav = resolveNav(catsSource, routeData.categoryAltUrlMap);

  const pending = lastResolvedNav ? (
    <NavSections
      cats={lastResolvedNav.cats}
      altUrlMap={lastResolvedNav.altUrlMap}
      host={host}
      lmnAllowed={routeData.lmnAllowed}
      lmnEnable={routeData.lmnEnable}
      openNavItem={openNavItem}
      onToggle={handleToggle}
    />
  ) : (
    <MainNavSkeleton />
  );

  return (
    <div {...attributes}>
      <SkeletonAwait resolve={nav} skeleton={pending}>
        {({ cats, altUrlMap }) => (
          <NavSections
            cats={cats}
            altUrlMap={altUrlMap}
            host={host}
            lmnAllowed={routeData.lmnAllowed}
            lmnEnable={routeData.lmnEnable}
            openNavItem={openNavItem}
            onToggle={handleToggle}
          />
        )}
      </SkeletonAwait>
    </div>
  );
}
